import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format, differenceInDays } from "date-fns";
import { ArrowLeft, ExternalLink, Plus, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ItemCard } from "@/components/items/ItemCard";
import { ItemEditSheet } from "@/components/items/ItemEditSheet";
import { useProjectBySlug } from "@/hooks/useProjects";
import { useProjectItems, useCreateItem } from "@/hooks/useItems";
import { useAuth } from "@/contexts/AuthContext";
import { useProcessDump } from "@/hooks/useItems";
import { toast } from "sonner";
import type { Item } from "@/lib/types";
import { formatError } from "@/lib/utils";

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: project, isLoading: projectLoading } = useProjectBySlug(slug);
  const { data: items, isLoading: itemsLoading } = useProjectItems(project?.name || "");
  const createItem = useCreateItem();
  const processDump = useProcessDump();

  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [newItemText, setNewItemText] = useState("");
  const [showAddItem, setShowAddItem] = useState(false);

  const stats = useMemo(() => {
    if (!items) return { total: 0, active: 0, completedThisWeek: 0, overdue: 0 };

    const today = new Date().toISOString().split("T")[0];
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekAgoStr = weekAgo.toISOString().split("T")[0];

    const active = items.filter((i) => i.status === "captured" || i.status === "active").length;
    const completedThisWeek = items.filter(
      (i) => i.status === "done" && i.updated_at >= weekAgoStr
    ).length;
    const overdue = items.filter(
      (i) =>
        (i.status === "captured" || i.status === "active") &&
        i.date_target &&
        i.date_target < today
    ).length;

    return { total: items.length, active, completedThisWeek, overdue };
  }, [items]);

  const activeItems = useMemo(
    () => items?.filter((i) => i.status === "captured" || i.status === "active") || [],
    [items]
  );
  const doneItems = useMemo(
    () => items?.filter((i) => i.status === "done") || [],
    [items]
  );

  const deadlineInfo = useMemo(() => {
    if (!project?.deadline) return null;
    const deadlineDate = new Date(project.deadline + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysLeft = differenceInDays(deadlineDate, today);

    let color = "text-muted-foreground";
    if (daysLeft < 0) color = "text-destructive";
    else if (daysLeft <= 7) color = "text-amber-500";
    else if (daysLeft <= 30) color = "text-yellow-500";

    let label = "";
    if (daysLeft < 0) label = `${Math.abs(daysLeft)}d overdue`;
    else if (daysLeft === 0) label = "Due today";
    else if (daysLeft === 1) label = "Due tomorrow";
    else label = `${daysLeft}d left`;

    return { daysLeft, color, label, formatted: format(deadlineDate, "MMM d, yyyy") };
  }, [project?.deadline]);

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemText.trim() || !project) return;

    const rawInput = newItemText.trim();
    createItem.mutate(
      { title: rawInput, raw_input: rawInput, source: "manual", project_tag: project.name },
      {
        onSuccess: (item) => {
          if (user) {
            processDump.mutate({ item_id: item.id, raw_input: rawInput, user_id: user.id });
          }
          setNewItemText("");
          setShowAddItem(false);
          toast("Item added to project");
        },
        onError: (err) => toast.error(formatError(err)),
      }
    );
  };

  if (projectLoading) {
    return (
      <AppLayout>
        <p className="text-muted-foreground text-sm">Loading project...</p>
      </AppLayout>
    );
  }

  if (!project) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Project not found.</p>
          <Button variant="outline" onClick={() => navigate("/projects")}>
            Back to Projects
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/projects")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div
            className="w-4 h-4 rounded-full shrink-0"
            style={{ backgroundColor: project.color }}
          />
          <h1 className="text-xl font-semibold flex-1">{project.name}</h1>
          {project.url && (
            <a
              href={project.url.startsWith("http") ? project.url : `https://${project.url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>

        {project.description && (
          <p className="text-sm text-muted-foreground mb-3 ml-12">
            {project.description}
          </p>
        )}

        {deadlineInfo && (
          <div className={`flex items-center gap-2 text-sm ml-12 ${deadlineInfo.color}`}>
            <Clock className="h-3.5 w-3.5" />
            <span>{deadlineInfo.formatted}</span>
            <Badge
              variant="secondary"
              className={`text-xs ${deadlineInfo.color}`}
            >
              {deadlineInfo.label}
            </Badge>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-lg border border-border p-3 bg-card text-center">
          <p className="text-2xl font-bold">{stats.active}</p>
          <p className="text-xs text-muted-foreground">Active</p>
        </div>
        <div className="rounded-lg border border-border p-3 bg-card text-center">
          <div className="flex items-center justify-center gap-1">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <p className="text-2xl font-bold">{stats.completedThisWeek}</p>
          </div>
          <p className="text-xs text-muted-foreground">Done this week</p>
        </div>
        <div className="rounded-lg border border-border p-3 bg-card text-center">
          <div className="flex items-center justify-center gap-1">
            {stats.overdue > 0 && <AlertTriangle className="h-4 w-4 text-amber-500" />}
            <p className="text-2xl font-bold">{stats.overdue}</p>
          </div>
          <p className="text-xs text-muted-foreground">Overdue</p>
        </div>
      </div>

      {/* Add item */}
      <div className="mb-4">
        {showAddItem ? (
          <form onSubmit={handleAddItem} className="flex items-center gap-2">
            <Input
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              placeholder={`Add item to ${project.name}...`}
              autoFocus
              className="flex-1"
            />
            <Button type="submit" size="sm" disabled={createItem.isPending || !newItemText.trim()}>
              Add
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowAddItem(false);
                setNewItemText("");
              }}
            >
              Cancel
            </Button>
          </form>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddItem(true)}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Item
          </Button>
        )}
      </div>

      {/* Active items */}
      {itemsLoading ? (
        <p className="text-muted-foreground text-sm">Loading items...</p>
      ) : (
        <div className="space-y-6">
          {activeItems.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">
                Active ({activeItems.length})
              </h3>
              <div className="space-y-2">
                <AnimatePresence mode="popLayout">
                  {activeItems.map((item) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      onClick={() => setSelectedItem(item)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}

          {doneItems.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">
                Completed ({doneItems.length})
              </h3>
              <div className="space-y-2 opacity-60">
                {doneItems.slice(0, 10).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border"
                  >
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                    <span className="text-sm line-through truncate">{item.title}</span>
                  </div>
                ))}
                {doneItems.length > 10 && (
                  <p className="text-xs text-muted-foreground text-center">
                    +{doneItems.length - 10} more completed items
                  </p>
                )}
              </div>
            </div>
          )}

          {activeItems.length === 0 && doneItems.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No items for this project yet.</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add items above or tag existing items with "{project.name}".
              </p>
            </div>
          )}
        </div>
      )}

      <ItemEditSheet
        item={selectedItem}
        open={!!selectedItem}
        onOpenChange={(open) => !open && setSelectedItem(null)}
      />
    </AppLayout>
  );
}
